// =============================================================================
// IMAGE-WITH-TEXT NOTE — client-side behaviour for the overlay "Toby's tidbit" note
// (blocks/_image-with-text-media.liquid). Mirrors the date + handwriting-reveal behaviour of
// src/js/objects/global/toby-tidbit.js (kept as a small, single-responsibility module rather than
// coupling the two sections). All progressive-enhancement.
//
//   1. DATE   — when the merchant leaves the note date BLANK the block renders a hidden
//               `[data-iwt-note-date]`; fill it with the visitor's machine LOCAL date (e.g.
//               "17th July 2026") and unhide it. A merchant-typed date is server-rendered static
//               text with no hook, so it is untouched.
//   2. REVEAL — on scroll-into-view (IntersectionObserver) run a left-to-right handwriting clip
//               reveal of the note title/content/date. Skipped (text shown immediately) when the
//               visitor prefers reduced motion or the browser lacks IntersectionObserver. The full
//               text always stays in the DOM (a11y / SEO).
//
// Dependency-free; bundled into assets/global.autojs.js via entry/global.js -> objects/global/
// index.js. Idempotent; re-runs on shopify:section:load (the theme editor re-renders the section).
// =============================================================================

const NOTE = '[data-iwt-note]';
const ANIMATE = 'image-with-text__overlay--animate';
const WRITING = 'is-writing';

// The visitor's LOCAL date in the designed "Do Month YYYY" shape (e.g. "17th July 2026").
// English brand voice, hand-assembled: Intl has no ordinal-day (1st/2nd/3rd) date format, and
// locale-dependent options reorder day/month per visitor — so pin en-GB for the month name only.
function localDateLabel(now) {
  try {
    const day = now.getDate();
    const suffix =
      day >= 11 && day <= 13
        ? 'th'
        : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
    const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(
      now
    );
    return `${day}${suffix} ${month} ${now.getFullYear()}`;
  } catch {
    return now.toDateString();
  }
}

function prefersReducedMotion() {
  return (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function fillDate(note, now) {
  const dateEl = note.querySelector('[data-iwt-note-date]');
  if (!dateEl) return; // a merchant-typed (static) date — nothing to fill
  dateEl.textContent = localDateLabel(now);
  dateEl.removeAttribute('hidden');
}

// Arm the handwriting reveal. Adding the animate class clips the text first; the IntersectionObserver
// then runs the reveal on scroll-in. Returns early (text stays visible) under reduced motion or with
// no IntersectionObserver.
function armReveal(note) {
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
  note.classList.add(ANIMATE);
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(WRITING);
        obs.unobserve(entry.target);
      });
    },
    { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
  );
  observer.observe(note);
}

function initNote(note, now) {
  if (note.dataset.iwtNoteInit === 'true') return; // idempotent across re-inits
  note.dataset.iwtNoteInit = 'true';
  armReveal(note); // clip first
  fillDate(note, now); // then fill, so the local date is never flashed un-clipped
}

function init(scope) {
  const context = scope && scope.querySelectorAll ? scope : document;
  const now = new Date();
  context.querySelectorAll(NOTE).forEach((note) => initNote(note, now));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor re-renders the section wrapper; re-init only within the reloaded section.
document.addEventListener('shopify:section:load', (event) =>
  init(event.target)
);

export default init;
